import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  ProgressFooter,
  ProgressFooterActions,
  ProgressFooterSaveNotification,
} from "../get-compliant/ProgressFooter";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";

import { Routes } from "../../root/routes";
import {
  UseDataRetentionModule,
  useDataRetentionModule,
} from "../../components/organisms/data-retention/DataRetentionModule";
import {
  UseEditPrivacyCenter,
  useEditPrivacyCenter,
} from "../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterNotice } from "../../hooks/usePrivacyCenterNotice";
import { RetentionPolicyEditor } from "../stay-compliant/privacy-center/us-state-notices/RetentionPolicyEditor";
import { PageLoading } from "../../../common/components/Loading";
import { Breadcrumbs } from "@truevault/aquilo";
import { PrivacyCenterNoticeDto } from "../../../common/service/server/dto/PrivacyCenterDto";

export function StayCompliantDataRetentionPage() {
  const history = useHistory();
  const editPrivacyCenter = useEditPrivacyCenter();

  const orgId = editPrivacyCenter?.props?.org?.id;
  const privacyCenterId = editPrivacyCenter?.props?.privacyCenter?.id;

  const [draftNotice, draftNoticeRequest, refreshDraftNotice] = usePrivacyCenterNotice(
    orgId,
    privacyCenterId,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );

  const dataRetentionModule = useDataRetentionModule();
  const [isSavingDefaults, setIsSavingDefaults] = useState(false);

  const [showRetentionPolicyHelpModal, setShowRetentionPolicyHelpModal] = useState(null);

  useEffect(() => {
    if (
      showRetentionPolicyHelpModal === null &&
      draftNotice &&
      editPrivacyCenter &&
      dataRetentionModule
    ) {
      const isAlreadyPublished = editPrivacyCenter?.props?.allNotices?.some(
        (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
      );
      const isFinished = isDataRetentionComplete(
        draftNotice,
        editPrivacyCenter,
        dataRetentionModule,
      );
      // Don't show the help modal if the section is finished or if user has already published their privacy policy
      setShowRetentionPolicyHelpModal(!isAlreadyPublished && !isFinished);
    }
  }, [
    draftNotice,
    editPrivacyCenter,
    dataRetentionModule,
    showRetentionPolicyHelpModal,
    setShowRetentionPolicyHelpModal,
  ]);

  if (!draftNotice || !editPrivacyCenter || !dataRetentionModule) {
    return <PageLoading />;
  }

  const isAlreadyPublished = editPrivacyCenter?.props?.allNotices?.some(
    (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
  );
  const isRetentionPolicySectionFinished =
    isDataRetentionComplete(draftNotice, editPrivacyCenter, dataRetentionModule) ||
    isAlreadyPublished;

  return (
    <StandardPageWrapper
      requests={[
        dataRetentionModule.requests.dataMapRequest,
        dataRetentionModule.requests.dataRetentionRequest,
        draftNoticeRequest,
      ]}
      footer={
        <ProgressFooter
          content={<ProgressFooterSaveNotification request={dataRetentionModule.updateRequest} />}
          actions={
            <ProgressFooterActions
              nextContent="Done"
              nextOnClick={async () => {
                setIsSavingDefaults(true);
                try {
                  await dataRetentionModule.saveDefaultDescriptions();
                } finally {
                  setIsSavingDefaults(false);
                }
                history.push(Routes.dataMap.dashboard);
              }}
              nextLoading={isSavingDefaults}
              nextDisabled={!isRetentionPolicySectionFinished || isSavingDefaults}
              nextDisabledTooltip={
                draftNotice.useCustomRetentionPolicySection
                  ? "Add a custom retention policy"
                  : dataRetentionModule.disabledTooltip
              }
            />
          }
        />
      }
      fullWidth
    >
      <div className="tw-boundary" style={{ marginBottom: "32px" }}>
        <Breadcrumbs
          hideHomeIcon={true}
          title="Data Retention"
          source="Data Map"
          sourceRoute={Routes.dataMap.dashboard}
        />
      </div>

      <RetentionPolicyEditor
        draftNotice={draftNotice}
        refreshNotice={refreshDraftNotice}
        organizationId={orgId}
        privacyCenterId={privacyCenterId}
        dataRetentionModule={dataRetentionModule}
        showHelpModal={false}
        setShowHelpModal={() => {}}
      />
    </StandardPageWrapper>
  );
}

export const isDataRetentionComplete = (
  draftNotice: PrivacyCenterNoticeDto,
  editPrivacyCenter: UseEditPrivacyCenter,
  dataRetentionModule: UseDataRetentionModule,
): boolean => {
  const customRetentionPolicySection = draftNotice.sections.find(
    (s) => s.anchor === "data-retention-policy",
  );
  return draftNotice.useCustomRetentionPolicySection
    ? customRetentionPolicySection?.content.length > 2
    : dataRetentionModule.isFinished;
};
